import React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { PaymentMetricsSummary } from '@/types/domain/payment-management';
import { formatInr } from '@/lib/utils';
import {
  IndianRupee,
  CheckCircle2,
  Clock,
  Calendar,
  Layers,
  FileCheck,
} from 'lucide-react';

interface PaymentKpiCardsProps {
  metrics: PaymentMetricsSummary;
  onSelectFilter?: (filterType: string, value: string) => void;
}

export const PaymentKpiCards: React.FC<PaymentKpiCardsProps> = ({ metrics, onSelectFilter }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5 font-poppins">
      {/* Card 1: Total Disbursed */}
      <Card
        onClick={() => onSelectFilter?.('status', 'ALL')}
        className="rounded-2xl border-border bg-card hover:border-primary/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-foreground-secondary">
              Total Disbursed
            </span>
            <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-600 group-hover:scale-110 transition-transform">
              <IndianRupee className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-foreground">{metrics.totalPayments}</span>
            <span className="text-[10px] text-foreground-muted font-bold truncate">
              {formatInr(metrics.totalDisbursedValue)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Card 2: Completed / Paid */}
      <Card
        onClick={() => onSelectFilter?.('status', 'PAID')}
        className="rounded-2xl border-border bg-card hover:border-emerald-500/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-emerald-600">
              Paid / Cleared
            </span>
            <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 group-hover:scale-110 transition-transform">
              <CheckCircle2 className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-emerald-600">{metrics.paidCount}</span>
            <span className="text-[10px] text-emerald-600/80 font-bold">fully cleared</span>
          </div>
        </CardContent>
      </Card>

      {/* Card 3: Scheduled Disbursements */}
      <Card
        onClick={() => onSelectFilter?.('status', 'SCHEDULED')}
        className="rounded-2xl border-border bg-card hover:border-purple-500/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-purple-600">
              Scheduled Runs
            </span>
            <div className="p-1.5 rounded-lg bg-purple-500/10 text-purple-600 group-hover:scale-110 transition-transform">
              <Calendar className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-purple-700">{metrics.scheduledCount}</span>
            <span className="text-[10px] text-purple-600/80 font-bold">in payment pool</span>
          </div>
        </CardContent>
      </Card>

      {/* Card 4: Partial Settlements */}
      <Card
        onClick={() => onSelectFilter?.('status', 'PARTIALLY_PAID')}
        className="rounded-2xl border-border bg-card hover:border-amber-500/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-amber-600">
              Partial Payments
            </span>
            <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-600 group-hover:scale-110 transition-transform">
              <Layers className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-amber-600">{metrics.partiallyPaidCount}</span>
            <span className="text-[10px] text-amber-600/80 font-bold">split settlements</span>
          </div>
        </CardContent>
      </Card>

      {/* Card 5: Overdue Payments */}
      <Card
        onClick={() => onSelectFilter?.('isOverdue', 'true')}
        className="rounded-2xl border-border bg-card hover:border-rose-500/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-rose-600">
              Overdue Payables
            </span>
            <div className="p-1.5 rounded-lg bg-rose-500/10 text-rose-600 group-hover:scale-110 transition-transform">
              <Clock className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-rose-600">{metrics.overdueCount}</span>
            <span className="text-[10px] text-rose-600/80 font-bold truncate">
              {formatInr(metrics.overduePayablesValue)}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Card 6: Reconciliation Required / Failed */}
      <Card
        onClick={() => onSelectFilter?.('status', 'RECONCILIATION_REQUIRED')}
        className="rounded-2xl border-border bg-card hover:border-amber-500/40 transition-all cursor-pointer shadow-2xs group"
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-extrabold tracking-wider uppercase text-amber-600">
              Reconciliation
            </span>
            <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-600 group-hover:scale-110 transition-transform">
              <FileCheck className="h-4 w-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-amber-600">
              {metrics.reconciliationRequiredCount + metrics.failedCount}
            </span>
            <span className="text-[10px] text-amber-600/80 font-bold">audit variance</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
